import { z } from 'zod';
import type { Session, SessionPersistence } from './types';

const STORAGE_KEY = 'oaa-bi.session';

/** Access tokens closer than this to expiry count as expired (they are renewed first). */
const MIN_REMAINING_MS = 30_000;

const sessionSchema = z.object({
  accessToken: z.string().min(1),
  tokenType: z.string().min(1),
  expiresAt: z.iso.datetime({ offset: true }),
  sessionExpiresAt: z.iso.datetime({ offset: true }).nullish(),
  user: z.object({
    id: z.string().min(1),
    email: z.string(),
    displayName: z.string(),
    createdAt: z.string(),
  }),
});

function getStorage(persistence: SessionPersistence): Storage | null {
  try {
    return persistence === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    // Storage can be unavailable (privacy mode, sandboxed iframes).
    return null;
  }
}

export function isSessionExpired(session: Pick<Session, 'expiresAt'>, now = Date.now()): boolean {
  return Date.parse(session.expiresAt) - now <= MIN_REMAINING_MS;
}

/**
 * Parses a stored value; returns null for anything malformed. An expired access token is still
 * returned: the refresh cookie can renew it, and the API decides whether the session has ended.
 */
export function parseStoredSession(raw: string | null): Session | null {
  if (raw === null) return null;
  try {
    const result = sessionSchema.safeParse(JSON.parse(raw));
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

/** The stored session of this tab, or the shared "keep me signed in" one. */
export function loadPersistedSession(): Session | null {
  for (const persistence of ['session', 'local'] as const) {
    const storage = getStorage(persistence);
    const session = parseStoredSession(storage?.getItem(STORAGE_KEY) ?? null);
    if (session) return session;
    storage?.removeItem(STORAGE_KEY);
  }
  return null;
}

/** Where the current session is stored: this tab's storage if it has one, otherwise the shared one. */
export function storedPersistence(): SessionPersistence {
  try {
    return getStorage('session')?.getItem(STORAGE_KEY) ? 'session' : 'local';
  } catch {
    return 'local';
  }
}

export function persistSession(session: Session, persistence: SessionPersistence): void {
  // Overwrite in place: removing the shared value first would look like a sign-out to other tabs.
  getStorage(persistence === 'local' ? 'session' : 'local')?.removeItem(STORAGE_KEY);
  try {
    getStorage(persistence)?.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Quota exceeded or storage disabled: the session simply lasts for this page only.
  }
}

export function clearPersistedSession(): void {
  getStorage('local')?.removeItem(STORAGE_KEY);
  getStorage('session')?.removeItem(STORAGE_KEY);
}

export interface CrossTabSessionChange {
  /** The session now stored by the other tab, or null when it signed out. */
  session: Session | null;
  /** Access token that was stored before the change, if any. */
  previousAccessToken: string | null;
}

function readAccessToken(raw: string | null): string | null {
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' &&
      parsed !== null &&
      'accessToken' in parsed &&
      typeof parsed.accessToken === 'string'
      ? parsed.accessToken
      : null;
  } catch {
    return null;
  }
}

/** Notifies when another tab signs in or out (only `local` sessions are shared across tabs). */
export function subscribeToSessionChangesInOtherTabs(
  onChange: (change: CrossTabSessionChange) => void,
): () => void {
  const handler = (event: StorageEvent) => {
    if (event.storageArea !== getStorage('local') || event.key !== STORAGE_KEY) return;
    onChange({
      session: parseStoredSession(event.newValue),
      previousAccessToken: readAccessToken(event.oldValue),
    });
  };
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener('storage', handler);
  };
}
